import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtTL, ORDER_STATUS } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import CriticalPinDialog from "../CriticalPinDialog";

const SHIP_TR = { pending: "Bekliyor", shipped: "Yolda", delivered: "Teslim", cancelled: "İptal" };
const INV_TR = { issued: "Kesildi", paid: "Ödendi", cancelled: "İptal" };

export default function OrdersTab() {
    const [orders, setOrders] = useState(null);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [pinTarget, setPinTarget] = useState(null);
    useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => setOrders([])); }, []);

    const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));

    const deleteRecord = async (pin) => {
        try {
            await api.delete(`/admin/records/orders/${pinTarget.order_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success(`${pinTarget.order_no} silindi`);
            setPinTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        }
    };

    const filtered = (orders || [])
        .filter((o) => statusFilter === "all" || o.status === statusFilter)
        .filter((o) => !q || o.order_no.toLowerCase().includes(q.toLowerCase()) || o.supplier_name.toLowerCase().includes(q.toLowerCase()) || o.buyer_company_name.toLowerCase().includes(q.toLowerCase()));

    if (!orders) return <div className="h-64 animate-pulse rounded-2xl bg-muted" />;

    return (
        <div data-testid="admin-orders-tab">
            <div className="mb-4 flex items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">Sipariş → sevkiyat → fatura zinciri (salt görüntüleme).</p>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sipariş no, tedarikçi, mağaza ara..." className="h-9 w-72 rounded-md border bg-background px-3 text-sm" data-testid="admin-order-search" />
            </div>
            <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="admin-order-status-filters">
                {[["all", "Tümü"], ...Object.entries(ORDER_STATUS).map(([k, v]) => [k, v.label])].map(([v, l]) => (
                    <button key={v} onClick={() => setStatusFilter(v)} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${statusFilter === v ? "border-primary bg-primary text-primary-foreground" : "hover:border-terra-500/50"}`} data-testid={`order-filter-${v}`}>{l}</button>
                ))}
                <span className="ml-auto text-xs text-muted-foreground">{filtered.length} sipariş</span>
            </div>
            <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                <div className="max-h-[520px] overflow-x-auto overflow-y-auto">
                    <table className="w-full text-sm" data-testid="admin-orders-table">
                        <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                            <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                                <th className="p-3">Sipariş</th><th className="p-3">Taraflar</th><th className="p-3">Tutar</th><th className="p-3">Sipariş</th><th className="p-3">Sevkiyat</th><th className="p-3">Fatura</th><th className="p-3">Tarih</th><th className="p-3 text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((o) => (
                                <tr key={o.order_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`admin-order-${o.order_id}`}>
                                    <td className="p-3"><Link to={`/panel/siparis/${o.order_id}`} className="font-mono font-semibold text-primary hover:underline">{o.order_no}</Link></td>
                                    <td className="p-3 text-xs text-muted-foreground">{o.supplier_name} → {o.buyer_company_name}</td>
                                    <td className="p-3 font-display font-bold text-terra-600">{fmtTL(o.total)}</td>
                                    <td className="p-3"><Badge variant="outline" className={`text-[10px] ${ORDER_STATUS[o.status]?.cls}`}>{ORDER_STATUS[o.status]?.label || o.status}</Badge></td>
                                    <td className="p-3 text-xs">{o.shipment ? SHIP_TR[o.shipment.status] || o.shipment.status : "—"}</td>
                                    <td className="p-3 text-xs">{o.invoice ? `${INV_TR[o.invoice.status] || o.invoice.status}${o.invoice.pdf_uploaded ? " • PDF" : ""}` : "—"}</td>
                                    <td className="p-3 text-xs text-muted-foreground">{fmtDate(o.created_at)}</td>
                                    <td className="p-3 text-right">
                                        <Button size="sm" variant="outline" className="h-8 border-red-200 text-xs text-red-600 hover:bg-red-50" onClick={() => setPinTarget(o)} data-testid={`order-delete-${o.order_id}`}>
                                            <Trash2 className="h-3 w-3" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {!filtered.length && (
                                <tr><td colSpan={8} className="p-16 text-center text-sm text-muted-foreground"><Package className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />Kayıt yok.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <CriticalPinDialog
                open={!!pinTarget}
                onClose={() => setPinTarget(null)}
                title="Sipariş Kaydını Sil"
                message={pinTarget ? `${pinTarget.order_no} (${pinTarget.supplier_name} → ${pinTarget.buyer_company_name}) kalıcı olarak silinecek. Bağlı sevkiyat/fatura kayıtları ayrıca silinmelidir. Bu işlem denetim kaydına alınır.` : ""}
                onSubmit={deleteRecord}
            />
        </div>
    );
}
